import { Flex, Icon, Skeleton, Tag, Text } from '@chakra-ui/react';
import {
  BusinessOverviewMetric,
  BusinessOverviewMetricValue,
  ChartDataFragment,
} from '@client/graphql/__generated__/types';
import { formatToCurrency } from '@common/utils/formatter';
import isNil from 'lodash/isNil';
import range from 'lodash/range';
import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';
import { useMemo } from 'react';
import { CardTab } from '~/common/components/CardTabs/CardTab';
import { ComingSoon } from '../../ComingSoon';
import { MetricBarChart } from '../../Listings/ListingPage/ListingPageMetrics/Charts/MetricBarChart';
import { DATE_RANGE_OPTIONS, TAB_DATA_MAP } from './constants';
import { useOrganizationPipelineContext } from './OrganizationPipelineContext';

export const OrganizationPipelineCardTabs = () => {
  const { summary, selectedTab, setSelectedTab, selectedDateRange } =
    useOrganizationPipelineContext();

  const periodLabel = DATE_RANGE_OPTIONS.find(
    (option) => option.value === selectedDateRange
  )?.label as string;

  const tabs = useMemo(() => {
    return summary?.data?.length
      ? [
          {
            metric: BusinessOverviewMetric.SIGNED_AGENCY_AGREEMENTS,
          },
          ...summary.data,
          {
            metric: BusinessOverviewMetric.OFFERS_CLOSED,
          },
        ]
      : [];
  }, [summary]);

  return (
    <Flex gap={3} justifyContent="space-between" mb={2} width="100%">
      {summary.loading &&
        !tabs.length &&
        range(0, 5).map((index) => (
          <Skeleton key={index} flexGrow={1} height="240px" />
        ))}
      {tabs.map((data) => {
        const tabMeta = TAB_DATA_MAP[data.metric];
        const label = tabMeta.label;

        if (tabMeta.isComingSoon || !('chart' in data)) {
          return (
            <CardTab
              key={data.metric}
              body={<ComingSoon />}
              header={<Text color="whiteAlpha.600">{label}</Text>}
              isSelected={false}
              onClick={() => {}}
            />
          );
        }

        return (
          <CardTab
            key={data.metric}
            body={<TabBody {...data} periodLabel={periodLabel} />}
            header={<Text color="whiteAlpha.600">{label}</Text>}
            isSelected={selectedTab === data.metric}
            onClick={() => {
              setSelectedTab(data.metric);
            }}
          />
        );
      })}
    </Flex>
  );
};

interface TabBodyProps {
  current: BusinessOverviewMetricValue;
  previous: BusinessOverviewMetricValue;
  estimatedDealValue?: number;
  periodLabel: string;
  chart: ChartDataFragment;
}

const TabBody = ({
  current,
  previous,
  estimatedDealValue,
  periodLabel,
  chart,
}: TabBodyProps) => {
  const changeColor =
    isNil(previous.value) ||
    isNil(current.value) ||
    previous.value === current.value
      ? 'whiteAlpha.600'
      : current.value > previous.value
        ? 'green.500'
        : 'red.500';
  const changeValue =
    isNil(current.value) ||
    isNil(previous.value) ||
    current.value === previous.value
      ? 0
      : current.value - previous.value;

  return (
    <Flex direction="column" gap={4} height="100%">
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="flex-end" gap={2}>
          <Text fontSize="48px" fontWeight="medium" lineHeight={1}>
            {current.value}
          </Text>
          <Flex alignItems="center" gap={1}>
            {!!changeValue && (
              <Icon
                as={changeValue > 0 ? TrendingUpIcon : TrendingDownIcon}
                color={changeColor}
              />
            )}
            <Text
              color={changeColor}
              fontSize="sm"
              fontWeight="medium"
              letterSpacing="0.88px"
              textTransform="uppercase"
            >
              {changeValue
                ? `${Math.abs(changeValue)} ${periodLabel}`
                : 'No Change'}
            </Text>
          </Flex>
        </Flex>
        {estimatedDealValue && (
          <Tag borderRadius="4px" size="sm">
            <Text color="cyan.500" fontWeight="medium">
              {formatToCurrency(estimatedDealValue, 2, 'compact')}
            </Text>
          </Tag>
        )}
      </Flex>
      {chart && (
        <Flex direction="column" gap={2} minHeight="100px">
          <MetricBarChart
            data={chart.data}
            labelUnit={chart.labelUnit}
            valueUnit={chart.valueUnit}
          />
        </Flex>
      )}
    </Flex>
  );
};
